import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { JUZZ_LIST } from "@/lib/juzz";
import { SURAH_LIST, juzzForPage } from "@/lib/surahs";
import { useSeo, orgSchema, websiteSchema, faqSchema, howToSchema, speakableSchema, definedTermSchema } from "@/lib/seo";
import { BookOpen, Sparkles, Search, LayoutGrid, GraduationCap, Users, ShieldCheck, ArrowRight, Headphones, Bookmark, Globe2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FaqAccordion } from "@/components/FaqAccordion";
import { RecitationActivityBanner } from "@/components/RecitationActivityWidget";
import { ShareAppButton } from "@/components/PwaActions";

const HOME_FAQS = [
  { q: "Can I read the Quran online for free on EasyQuran.UK?",
    a: "Yes — the full Quran is free to read online, in beautiful 15-line Madani Mushaf style. You can browse by Juzz (Para) or jump directly to any of the 114 Surahs, with verse-by-verse Arabic recitation and English / Urdu translations." },
  { q: "How do I read the Quran by Juzz (Para)?",
    a: "Use the \"Jump to Juzz\" search box on the home page, or open the Juzz menu in the top navigation. You can search Juzz by number (1-30) or by name (e.g. Alif Lam Meem, Amma, Tabarak)." },
  { q: "Can I read the Quran by Surah?",
    a: "Yes. Use the \"Jump to Surah\" search box — type any Surah name (e.g. Yaseen, Al-Mulk, Ar-Rahman) or its number, and we'll take you straight to the right Mushaf page." },
  { q: "Does the website work on mobile?",
    a: "EasyQuran.UK is fully responsive — pages, search boxes and recitation controls are designed mobile-first so you can read the Quran online on iPhone, Android or any tablet without zooming." },
  { q: "Can I bookmark verses and continue reading later?",
    a: "Yes. Sign in (free) and tap the bookmark icon on any ayah while reading. All your bookmarks sync to the cloud and appear under the clip icon in the top navigation, so you can resume from the same ayah on any device." },
  { q: "Do you also teach the Quran online with a live teacher?",
    a: "Yes — once you start reading, you can book live one-to-one online Quran classes with verified male or female teachers. We offer Nazra, Tajweed, Hifz and Tafsir classes for kids and adults worldwide. The first trial class is free." },
  { q: "Which font is used for the Arabic text?",
    a: "We use a 15-line Uthmani Madani Mushaf script by default, with an option to switch to Indo-Pak (PDMS Saleem) script — same layout used in Tāj Company printed Qurans common across South Asia." },
  { q: "Is recitation audio available?",
    a: "Yes — verse-by-verse audio from world-renowned reciters including Mishary Alafasy and Shamshad Ali Khan, plus the ability to switch reciters mid-session." },
];

// Big searchable combobox — used for both Juzz and Surah quick-jump.
const SearchableCombo = ({
  label, icon: Icon, placeholder, options, renderOption, onPick, testId,
}) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter(o => o._search.includes(s));
  }, [q, options]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.18em] text-accent mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && matches[0]) { onPick(matches[0]); setOpen(false); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder}
          className="pl-12 pr-4 h-14 sm:h-16 rounded-2xl text-base sm:text-lg border-2 border-accent/40 focus-visible:border-accent shadow-sm"
          inputMode="search"
          data-testid={`${testId}-input`}
          aria-label={label}
        />
      </div>
      {open && matches.length > 0 && (
        <ul
          className="absolute z-40 left-0 right-0 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-border bg-popover shadow-2xl divide-y divide-border"
          data-testid={`${testId}-options`}
        >
          {matches.map(o => (
            <li key={o._key}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(o); setOpen(false); }}
                className="flex items-center w-full gap-3 px-4 py-3 hover:bg-accent/10 text-left"
                data-testid={`${testId}-option-${o._key}`}
              >
                {renderOption(o)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Six rotating ornate book cover styles so the shelf feels alive.
const COVER_STYLES = [
  {
    bg: "linear-gradient(160deg, #065F46 0%, #022c20 100%)",
    accent: "#D4AF37",
    pattern: "radial-gradient(circle at 50% 18%, rgba(212,175,55,0.18) 0%, transparent 35%), repeating-linear-gradient(45deg, rgba(212,175,55,0.05) 0 6px, transparent 6px 14px)",
  },
  {
    bg: "linear-gradient(160deg, #7c2d12 0%, #3b0a02 100%)",
    accent: "#F5D77A",
    pattern: "radial-gradient(circle at 50% 22%, rgba(245,215,122,0.22) 0%, transparent 38%), repeating-linear-gradient(-45deg, rgba(245,215,122,0.06) 0 6px, transparent 6px 14px)",
  },
  {
    bg: "linear-gradient(160deg, #1e3a8a 0%, #0b1a4a 100%)",
    accent: "#D4AF37",
    pattern: "radial-gradient(circle at 50% 20%, rgba(212,175,55,0.16) 0%, transparent 40%), repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 14px)",
  },
  {
    bg: "linear-gradient(160deg, #4c1d95 0%, #1f0a47 100%)",
    accent: "#E0BD63",
    pattern: "radial-gradient(circle at 50% 20%, rgba(224,189,99,0.18) 0%, transparent 38%), repeating-linear-gradient(-45deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 14px)",
  },
  {
    bg: "linear-gradient(160deg, #134e4a 0%, #042f2e 100%)",
    accent: "#D4AF37",
    pattern: "radial-gradient(circle at 50% 22%, rgba(212,175,55,0.2) 0%, transparent 36%), repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 14px)",
  },
  {
    bg: "linear-gradient(160deg, #78350f 0%, #341803 100%)",
    accent: "#E5C16B",
    pattern: "radial-gradient(circle at 50% 22%, rgba(229,193,107,0.22) 0%, transparent 40%), repeating-linear-gradient(-45deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 14px)",
  },
];

const Book = ({ juzz }) => {
  const style = COVER_STYLES[(juzz.number - 1) % COVER_STYLES.length];
  return (
    <Link
      to={`/quran/mushaf/juzz/${juzz.number}`}
      data-testid={`juzz-book-${juzz.number}`}
      className="group relative block"
      aria-label={`Open Juzz ${juzz.number} ${juzz.name_translit}`}
    >
      {/* The book */}
      <div
        className="relative aspect-[5/7] w-full rounded-md overflow-hidden shadow-[0_18px_30px_-12px_rgba(0,0,0,0.55)] transition-transform duration-300 group-hover:-translate-y-3 group-hover:rotate-[-1deg]"
        style={{ background: style.bg }}
      >
        <div className="absolute inset-0 opacity-90" style={{ backgroundImage: style.pattern }} />
        <div className="absolute inset-y-0 left-0 w-2 bg-black/30" />
        <div className="absolute inset-y-0 left-2 w-px bg-white/15" />
        <div className="absolute inset-3 rounded-sm border pointer-events-none" style={{ borderColor: `${style.accent}66` }} />
        <div className="absolute inset-[14px] rounded-[2px] border pointer-events-none" style={{ borderColor: `${style.accent}33` }} />

        <div className="absolute top-5 left-0 right-0 flex items-center justify-center gap-1.5" style={{ color: style.accent }}>
          <span className="h-px w-6" style={{ background: style.accent }} />
          <span className="text-[10px]">❋</span>
          <span className="h-px w-6" style={{ background: style.accent }} />
        </div>

        <div className="absolute top-[14%] left-1/2 -translate-x-1/2 flex items-center justify-center">
          <div
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center"
            style={{
              background: `radial-gradient(circle, ${style.accent} 0%, ${style.accent}99 100%)`,
              border: `1.5px solid ${style.accent}`,
              color: "#1a1a1a",
            }}
          >
            <span className="leading-none text-base sm:text-lg font-semibold">{juzz.number}</span>
          </div>
        </div>

        <div className="absolute top-[44%] left-0 right-0 px-3 text-center" style={{ color: style.accent }}>
          <p className="font-arabic text-xl sm:text-2xl leading-tight" dir="rtl">{juzz.name_arabic}</p>
        </div>

        <div className="absolute bottom-7 left-0 right-0 text-center px-2">
          <p className="font-display text-xs sm:text-sm uppercase tracking-widest text-white/90 leading-tight">
            Juzz {juzz.number}
          </p>
          <p className="font-display text-[11px] sm:text-xs text-white/65 mt-0.5 truncate px-2">
            {juzz.name_translit}
          </p>
        </div>

        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5" style={{ color: style.accent }}>
          <span className="h-px w-6" style={{ background: style.accent }} />
          <span className="text-[10px]">❋</span>
          <span className="h-px w-6" style={{ background: style.accent }} />
        </div>

        <div className="absolute inset-y-0 left-0 w-1/3 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.07), transparent)" }} />
      </div>

      <div className="mx-auto mt-1 h-1.5 w-[88%] rounded-full bg-black/25 blur-[3px]" />
    </Link>
  );
};

const Shelf = ({ books }) => (
  <div className="relative pb-6">
    <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-x-4 sm:gap-x-5 gap-y-8 sm:gap-y-0 px-4 sm:px-8 pt-4">
      {books.map((b) => <Book key={b.number} juzz={b} />)}
    </div>
    <div
      className="relative h-5 sm:h-6 rounded-sm mt-[-2px]"
      style={{
        background: "linear-gradient(180deg, #a16633 0%, #6b3a16 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -2px 4px rgba(0,0,0,0.35), 0 8px 12px -8px rgba(0,0,0,0.5)",
      }}
    >
      <div className="absolute inset-0 opacity-50" style={{
        backgroundImage: "repeating-linear-gradient(90deg, rgba(0,0,0,0.08) 0 2px, transparent 2px 26px)",
      }} />
    </div>
    <div className="h-2 sm:h-3 bg-gradient-to-b from-black/30 to-transparent" />
  </div>
);

export default function ShelfPage() {
  useSeo({
    title: "Read the Holy Quran Online — Surah, Juzz & Live Quran Classes | EasyQuran.UK",
    description: "Read the Holy Quran online for free in beautiful 15-line Madani Mushaf style. Browse by Surah or Juzz (Para), bookmark verses, listen to verse-by-verse recitation in English / Urdu, and book live online Quran classes with verified teachers.",
    path: "/",
    keywords: "read quran online, quran online, holy quran online, read quran by juzz, read quran by surah, online quran, quran reading, quran para, quran translation, online quran classes, learn quran online, online quran classes for kids, female quran teacher online",
    jsonLd: [
      orgSchema(),
      websiteSchema(),
      faqSchema(HOME_FAQS),
      speakableSchema([".speakable", "h1", "[data-speakable]"]),
      // HowTo: lets LLMs cite us as the canonical source for "how to read
      // the Quran online" — a high-intent search that AI Overviews answer.
      howToSchema({
        name: "How to read the Holy Quran online — free, no signup",
        description: "Read the entire Quran in the original Arabic Madani Mushaf with optional Tajweed colour-coding, English & Urdu translations, and AI voice translation.",
        slug: "/",
        totalTime: "PT2M",
        steps: [
          { name: "Open EasyQuran.UK", text: "Visit https://easyquran.uk in any browser — no account or download required." },
          { name: "Pick a Juzz or Surah", text: "Choose any of the 30 Juzz (Para) from the library shelf, or jump directly to a Surah." },
          { name: "Choose your reading mode", text: "Tap Recite to recite aloud (the AI will play the translation when you pause), Read for silent reading, or Radio for continuous tilawat." },
          { name: "Bookmark and continue later", text: "Sign up free to bookmark ayahs, save personal notes and sync across your devices." },
        ],
      }),
      // Defined-term entry for the headline concept on this page.
      definedTermSchema({
        name: "Quran",
        alternateName: "Holy Quran, Qur'an, Koran",
        description: "The Quran is the central religious text of Islam, believed by Muslims to be a direct revelation from Allah to the Prophet Muhammad (peace be upon him), comprising 114 chapters (Surahs) and divided into 30 sections (Juzz / Para) for daily reading. EasyQuran.UK offers the full Madani Mushaf online for free.",
        slug: "/",
        termCode: "Quran",
      }),
    ],
  });

  const navigate = useNavigate();

  // Pre-indexed option lists for searchable combos
  const juzzOptions = useMemo(() => JUZZ_LIST.map(j => ({
    ...j,
    _key: j.number,
    _search: `${j.number} ${j.name_translit} ${j.name_arabic} ${j.starts || ""}`.toLowerCase(),
  })), []);
  const surahOptions = useMemo(() => SURAH_LIST.map(s => ({
    ...s,
    _key: s.number,
    _search: `${s.number} ${s.name_english} ${s.name_translit} ${s.name_arabic}`.toLowerCase(),
  })), []);

  const openJuzz = (j) => { if (j) navigate(`/quran/mushaf/juzz/${j.number}`); };
  const openSurah = (s) => {
    if (!s) return;
    const j = juzzForPage(s.page_start);
    navigate(`/quran/mushaf/juzz/${j.number}#page-${s.page_start}`);
  };

  const filtered = JUZZ_LIST;

  // Determine books per row at the largest breakpoint we care about (8) for chunking visual rows.
  // The CSS grid itself handles responsive sizing; we keep one big grid per "shelf" wave.
  const rows = useMemo(() => {
    const out = [];
    const perRow = 8; // chunk for plank rendering on xl
    for (let i = 0; i < filtered.length; i += perRow) {
      out.push(filtered.slice(i, i + perRow));
    }
    return out;
  }, [filtered]);

  return (
    <div data-testid="shelf-page" className="relative w-full">
      {/* Library room background */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(120,67,28,0.18), transparent 60%), linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)",
        }}
      />

      {/* Compact top section: title + two big searchable jump boxes */}
      <div className="w-full px-3 sm:px-5 lg:px-8 pt-5 sm:pt-8">
        <header className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-widest text-accent">
            <Sparkles className="w-3.5 h-3.5" /> The Mushaf Library
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 leading-tight" data-testid="shelf-title">
            Read the Holy Quran Online — by Surah, by Juzz, or with a live teacher.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-foreground/70 max-w-2xl mx-auto">
            Free online Quran reading in beautiful 15-line Madani Mushaf style. Browse all 114 Surahs and 30 Juzz, listen to verse-by-verse recitation, bookmark ayāt — and when you&apos;re ready, book live online Quran classes with verified teachers worldwide.
          </p>
        </header>

        {/* In-short TL;DR moved to the global Footer so every page (not just
            landing) ships an LLM-quotable summary — stronger for GEO. */}

        {/* Two BIG searchable jump boxes — Juzz + Surah (full-width on landing) */}
        <div
          className="mt-5 sm:mt-7 w-full grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5"
          data-testid="quick-start-section"
        >
          <SearchableCombo
            label="Jump to Juzz"
            icon={LayoutGrid}
            placeholder="Search Juzz — number or name (e.g. 5, Sayaqul, Amma)…"
            options={juzzOptions}
            testId="quick-start-juzz"
            onPick={openJuzz}
            renderOption={(j) => (
              <>
                <span className="w-9 h-9 rounded-full bg-primary/10 border border-accent/40 inline-flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                  {j.number}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-base font-medium truncate">Juzz {j.number} · {j.name_translit}</span>
                  <span className="block text-xs text-foreground/55 truncate">{j.starts}</span>
                </span>
                <span className="font-arabic text-lg text-foreground/85 hidden sm:inline" dir="rtl">{j.name_arabic}</span>
              </>
            )}
          />

          <SearchableCombo
            label="Jump to Surah"
            icon={BookOpen}
            placeholder="Search Surah — try Yaseen, Al-Mulk, Rahman, 36…"
            options={surahOptions}
            testId="quick-start-surah"
            onPick={openSurah}
            renderOption={(s) => (
              <>
                <span className="w-9 h-9 rounded-full bg-primary/10 border border-accent/40 inline-flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                  {s.number}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-base font-medium truncate">{s.name_english}</span>
                  <span className="block text-xs text-foreground/55 truncate">{s.name_translit} · {s.total_ayahs} ayāt</span>
                </span>
                <span className="font-arabic text-lg text-foreground/85 hidden sm:inline" dir="rtl">{s.name_arabic}</span>
              </>
            )}
          />
        </div>

        {/* Continue-reading card removed per product request — bookmarks live in the header clip icon. */}
      </div>

      {/* Bookshelf — edge to edge */}
      <div className="w-full px-2 sm:px-4 lg:px-6 mt-6 sm:mt-8 pb-12">
        <div
          className="rounded-2xl border border-amber-900/30 overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #c98a4d 0%, #a3673a 50%, #874e22 100%)",
            backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.04), transparent 30%), repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0 3px, transparent 3px 14px)",
            boxShadow: "inset 0 0 60px rgba(0,0,0,0.35), 0 30px 60px -30px rgba(0,0,0,0.55)",
          }}
          data-testid="bookshelf"
        >
          <div className="h-3 sm:h-4" style={{ background: "linear-gradient(180deg, #5d3210, #b1733e)" }} />

          <div className="py-2">
            {rows.map((r, i) => (
              <Shelf key={i} books={r} />
            ))}
          </div>

          <div className="h-3 sm:h-4" style={{ background: "linear-gradient(180deg, #b1733e, #5d3210)" }} />
        </div>

        <p className="mt-6 text-center text-xs text-foreground/60">
          Tip: Each Juzz opens at its first Mushaf page. Use the arrows in the reader to turn pages.
        </p>
      </div>

      {/* ── Live community banner — reciters online right now + lifetime
          totals + top countries. Powered by /api/recitation/activity. ── */}
      <RecitationActivityBanner />

      {/* ── Share invitation — surfaces right after the social-proof banner
          when emotional engagement peaks. Native share-sheet on mobile, copy
          fallback on desktop. ── */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 -mt-2 pb-6 text-center">
        <p className="text-foreground/70 text-sm sm:text-base mb-3">
          Found EasyQuran helpful? <span className="text-foreground">Share it with your family & friends</span> — every share is ṣadaqah jāriyah.
        </p>
        <ShareAppButton />
      </section>

      {/* ── SEO: "Why EasyQuran" feature row ───────────────────────────────── */}
      <section className="w-full px-4 sm:px-6 lg:px-10 py-12 sm:py-16 border-t border-border bg-card/30" aria-labelledby="why-eq">
        <div className="max-w-6xl mx-auto">
          <h2 id="why-eq" className="font-display text-3xl sm:text-4xl text-center">A peaceful place to read the Quran online</h2>
          <p className="mt-3 text-center text-foreground/70 max-w-2xl mx-auto">Every feature is designed around the way the Quran was meant to be read — slowly, beautifully, and with full focus on the text.</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: BookOpen, title: "Surah & Juzz reading", desc: "Browse all 114 Surahs and 30 Juzz with a 15-line Madani Mushaf layout — the layout most Muslims grew up with." },
              { icon: Headphones, title: "Verse-by-verse audio", desc: "Listen to world-renowned reciters including Mishary Alafasy & Shamshad Ali Khan. Switch reciters anytime." },
              { icon: Bookmark, title: "Bookmark & resume", desc: "Sign in to save any ayah and continue reading right where you stopped — across phone, tablet and laptop." },
              { icon: Globe2, title: "Madani & Indo-Pak scripts", desc: "Read in Uthmani Madani or PDMS Saleem (Indo-Pak / Tāj Company) script — one tap to switch." },
            ].map(f => (
              <div key={f.title} className="rounded-2xl border border-accent/30 bg-card p-5">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-accent/40 inline-flex items-center justify-center text-primary mb-3"><f.icon className="w-5 h-5" /></div>
                <h3 className="font-display text-lg leading-tight">{f.title}</h3>
                <p className="text-sm text-foreground/65 mt-1.5 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO: Live Quran Classes promo (conversion to Goal 2) ──────────── */}
      <section className="w-full px-4 sm:px-6 lg:px-10 py-12 sm:py-16" aria-labelledby="live-classes">
        <div className="max-w-6xl mx-auto rounded-3xl border border-accent/40 bg-primary/[0.06] p-6 sm:p-10 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent"><GraduationCap className="w-3.5 h-3.5" /> Live · 1-to-1 · Worldwide</div>
            <h2 id="live-classes" className="font-display text-3xl sm:text-4xl mt-3 leading-tight">Learn Quran Online with a live teacher</h2>
            <p className="mt-3 text-foreground/75 leading-relaxed">Once you&apos;ve started reading, take the next step — book live online Quran classes with verified male &amp; female teachers. Tajweed correction, Hifz programs, kids&apos; Quran classes and one-to-one tutoring for adults.</p>
            <ul className="mt-5 grid grid-cols-2 gap-y-2 gap-x-6 text-sm text-foreground/85">
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary" /> Verified teachers</li>
              <li className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Kids & adults</li>
              <li className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Free trial class</li>
              <li className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-primary" /> UK · US · CA · AU</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="pill-btn h-11 px-5 bg-primary hover:bg-primary/90" data-testid="home-book-trial-cta">
                <Link to="/lessons">Book Free Trial Class <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
              </Button>
              <Button asChild variant="outline" className="pill-btn h-11 px-5" data-testid="home-see-tutors-cta">
                <Link to="/lessons#course-types">See course types</Link>
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Quran Classes for Kids", to: "/lessons#course-types" },
              { label: "Tajweed Classes", to: "/lessons#course-types" },
              { label: "Hifz-ul-Quran", to: "/lessons#course-types" },
              { label: "Female Quran Teachers", to: "/lessons#course-types" },
              { label: "Quran for Adults", to: "/lessons#course-types" },
              { label: "Weekend Classes", to: "/lessons#course-types" },
              { label: "Online Quran Classes UK", to: "/online-quran-classes/uk" },
              { label: "Online Quran Classes USA", to: "/online-quran-classes/usa" },
            ].map(p => (
              <Link key={p.label} to={p.to} className="rounded-xl border border-accent/35 bg-card hover:border-accent transition-colors p-3 text-sm">
                <span className="block font-medium leading-tight">{p.label}</span>
                <span className="text-xs text-primary mt-1 inline-block">Explore →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO: FAQ ────────────────────────────────────────────────────── */}
      <section className="w-full px-4 sm:px-6 lg:px-10 pb-16" aria-labelledby="home-faq">
        <div className="max-w-4xl mx-auto">
          <h2 id="home-faq" className="font-display text-3xl sm:text-4xl text-center">Frequently asked questions</h2>
          <p className="mt-2 text-center text-foreground/70">Everything about reading the Quran online — and taking the next step with a live teacher.</p>
          <div className="mt-7">
            <FaqAccordion items={HOME_FAQS} idPrefix="home-faq" />
          </div>
        </div>
      </section>
    </div>
  );
}
